<template>
  <div>
    <div class="d-flex align-center mb-6">
      <div>
        <p class="text-2xl mb-0">Data Matching</p>
        <p class="text-subtitle-1 mb-0">
          Configure how Excel labels map to actual brand names
        </p>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        color="success"
        outlined
        class="mr-2"
        :disabled="masterBrands.length === 0"
        :loading="loading"
        @click="autoFillBrandNames"
      >
        <v-icon left>{{ icons.mdiAutoFix }}</v-icon>
        Auto-fill Empty Names
      </v-btn>
    </div>

    <v-alert v-if="statusMsg" type="info" dismissible class="mb-4" @input="statusMsg = ''">
      {{ statusMsg }}
    </v-alert>

    <v-alert v-if="masterBrands.length === 0" type="warning" class="mb-4">
      <strong>No master brands loaded!</strong> Import brands in "Infos Marques" first.
    </v-alert>

    <v-alert type="info" dense text class="mb-4">
      <strong>Auto-cleaning:</strong> For
      <code>audienceBrand</code> and
      <code>audiencePrint</code> sources, the pattern "Brand - XX jours " is automatically removed.
    </v-alert>

    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab>Active Rules ({{ matchingRules.length }})</v-tab>
      <v-tab>Deleted Rules ({{ deletedRules.length }})</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <v-card outlined class="mb-4">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filterSource"
                  :items="availableSources"
                  label="Filter by Source"
                  prepend-inner-icon="mdi-filter"
                  dense
                  hide-details
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filterExcelLabel"
                  label="Filter by Excel Label"
                  prepend-inner-icon="mdi-filter"
                  dense
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filterCleanedLabel"
                  label="Filter by Cleaned Label"
                  prepend-inner-icon="mdi-filter"
                  dense
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filterBrandName"
                  label="Filter by Brand Name"
                  prepend-inner-icon="mdi-filter"
                  dense
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
            </v-row>
            <div class="text-caption grey--text mt-2">
              Showing {{ filteredMatchingRules.length }} of {{ matchingRules.length }} rules
            </div>
          </v-card-text>
        </v-card>

        <v-card>
          <v-data-table
            :headers="headers"
            :items="filteredMatchingRules"
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
          >
            <template slot="item.excel_label" slot-scope="{ item }">
              <span class="text-caption grey--text">{{ item.excel_label }}</span>
            </template>
            <template slot="item.cleaned_label" slot-scope="{ item }">
              <v-chip
                v-if="item.cleaned_label && item.cleaned_label !== item.excel_label"
                small
                color="info"
              >
                {{ item.cleaned_label }}
              </v-chip>
              <span v-else class="text-caption grey--text">—</span>
            </template>
            <template slot="item.brand_name" slot-scope="{ item }">
              <v-autocomplete
                :value="item.brand_name"
                :items="masterBrandNames"
                dense
                hide-details
                outlined
                :class="{ 'warning-bg': !item.brand_name }"
                :disabled="masterBrands.length === 0"
                placeholder="Select brand..."
                clearable
                @change="updateRuleBrand(item.id, $event)"
              ></v-autocomplete>
            </template>
            <template slot="item.actions" slot-scope="{ item }">
              <v-btn
                icon
                small
                color="error"
                @click="confirmDeleteMatchingRule(item.id)"
              >
                <v-icon small>{{ icons.mdiDelete }}</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <v-tab-item>
        <v-card outlined class="mb-4">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filterDeletedSource"
                  :items="availableDeletedSources"
                  label="Filter by Source"
                  prepend-inner-icon="mdi-filter"
                  dense
                  hide-details
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filterDeletedExcelLabel"
                  label="Filter by Excel Label"
                  prepend-inner-icon="mdi-filter"
                  dense
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filterDeletedCleanedLabel"
                  label="Filter by Cleaned Label"
                  prepend-inner-icon="mdi-filter"
                  dense
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filterDeletedBrandName"
                  label="Filter by Brand Name"
                  prepend-inner-icon="mdi-filter"
                  dense
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
            </v-row>
            <div class="text-caption grey--text mt-2">
              Showing {{ filteredDeletedRules.length }} of {{ deletedRules.length }} deleted rules
            </div>
          </v-card-text>
        </v-card>

        <v-card>
          <v-data-table
            :headers="deletedHeaders"
            :items="filteredDeletedRules"
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
          >
            <template slot="item.excel_label" slot-scope="{ item }">
              <span class="text-caption grey--text">{{ item.excel_label }}</span>
            </template>
            <template slot="item.cleaned_label" slot-scope="{ item }">
              <v-chip
                v-if="item.cleaned_label && item.cleaned_label !== item.excel_label"
                small
                color="info"
              >
                {{ item.cleaned_label }}
              </v-chip>
              <span v-else class="text-caption grey--text">—</span>
            </template>
            <template slot="item.brand_name" slot-scope="{ item }">
              <span v-if="item.brand_name">{{ item.brand_name }}</span>
              <span v-else class="text-caption grey--text">—</span>
            </template>
            <template slot="item.actions" slot-scope="{ item }">
              <v-btn
                icon
                small
                color="success"
                @click="restoreRule(item.id)"
              >
                <v-icon small>{{ icons.mdiRestore }}</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                @click="confirmHardDeleteRule(item.id)"
              >
                <v-icon small>{{ icons.mdiDeleteForever }}</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this matching rule? You can restore it later from the "Deleted Rules" tab.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteMatchingRule">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="hardDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Permanently Delete</v-card-title>
        <v-card-text>
          Are you sure you want to <strong>permanently delete</strong> this matching rule? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="hardDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="hardDeleteRule">Delete Forever</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import {
  mdiAutoFix,
  mdiDelete,
  mdiDeleteForever,
  mdiRestore,
} from "@mdi/js";
import { mapGetters } from "vuex";
import axios from "@axios";

export default {
  name: "DataMatching",
  data() {
    return {
      loading: false,
      statusMsg: "",
      activeTab: 0,
      icons: {
        mdiAutoFix,
        mdiDelete,
        mdiDeleteForever,
        mdiRestore,
      },
      headers: [
        { text: "ID", value: "id", width: "80px" },
        { text: "Source", value: "source", width: "150px" },
        { text: "Excel Label (Original)", value: "excel_label" },
        { text: "Cleaned Label", value: "cleaned_label", width: "200px" },
        { text: "Brand Name", value: "brand_name", width: "380px" },
        { text: "Actions", value: "actions", sortable: false, width: "100px" },
      ],
      deletedHeaders: [
        { text: "ID", value: "id", width: "80px" },
        { text: "Source", value: "source", width: "150px" },
        { text: "Excel Label (Original)", value: "excel_label" },
        { text: "Cleaned Label", value: "cleaned_label", width: "200px" },
        { text: "Brand Name", value: "brand_name", width: "200px" },
        { text: "Actions", value: "actions", sortable: false, width: "100px" },
      ],
      matchingRules: [],
      deletedRules: [],
      filterSource: null,
      filterExcelLabel: "",
      filterCleanedLabel: "",
      filterBrandName: "",
      filterDeletedSource: null,
      filterDeletedExcelLabel: "",
      filterDeletedCleanedLabel: "",
      filterDeletedBrandName: "",
      deleteDialog: false,
      hardDeleteDialog: false,
      ruleToDelete: null,
      ruleToHardDelete: null,
    };
  },
  computed: {
    ...mapGetters("marketing", {
      masterBrands: "getBrands",
    }),
    masterBrandNames() {
      return this.masterBrands.map(function(b) {
        return b.brand_name;
      }).sort();
    },
    availableSources() {
      var sources = [];
      for (var i = 0; i < this.matchingRules.length; i++) {
        var source = this.matchingRules[i].source;
        if (source && sources.indexOf(source) === -1) {
          sources.push(source);
        }
      }
      return sources.sort();
    },
    availableDeletedSources() {
      var sources = [];
      for (var i = 0; i < this.deletedRules.length; i++) {
        var source = this.deletedRules[i].source;
        if (source && sources.indexOf(source) === -1) {
          sources.push(source);
        }
      }
      return sources.sort();
    },
    filteredMatchingRules() {
      var self = this;
      var filtered = self.matchingRules;

      if (self.filterSource) {
        filtered = filtered.filter(function(rule) {
          return rule.source === self.filterSource;
        });
      }

      if (self.filterExcelLabel) {
        var excelFilter = self.filterExcelLabel.toLowerCase();
        filtered = filtered.filter(function(rule) {
          return rule.excel_label &&
            rule.excel_label.toLowerCase().indexOf(excelFilter) !== -1;
        });
      }

      if (self.filterCleanedLabel) {
        var cleanedFilter = self.filterCleanedLabel.toLowerCase();
        filtered = filtered.filter(function(rule) {
          return rule.cleaned_label &&
            rule.cleaned_label.toLowerCase().indexOf(cleanedFilter) !== -1;
        });
      }

      if (self.filterBrandName) {
        var brandFilter = self.filterBrandName.toLowerCase();
        filtered = filtered.filter(function(rule) {
          return rule.brand_name &&
            rule.brand_name.toLowerCase().indexOf(brandFilter) !== -1;
        });
      }

      return filtered;
    },
    filteredDeletedRules() {
      var self = this;
      var filtered = self.deletedRules;

      if (self.filterDeletedSource) {
        filtered = filtered.filter(function(rule) {
          return rule.source === self.filterDeletedSource;
        });
      }

      if (self.filterDeletedExcelLabel) {
        var excelFilter = self.filterDeletedExcelLabel.toLowerCase();
        filtered = filtered.filter(function(rule) {
          return rule.excel_label &&
            rule.excel_label.toLowerCase().indexOf(excelFilter) !== -1;
        });
      }

      if (self.filterDeletedCleanedLabel) {
        var cleanedFilter = self.filterDeletedCleanedLabel.toLowerCase();
        filtered = filtered.filter(function(rule) {
          return rule.cleaned_label &&
            rule.cleaned_label.toLowerCase().indexOf(cleanedFilter) !== -1;
        });
      }

      if (self.filterDeletedBrandName) {
        var brandFilter = self.filterDeletedBrandName.toLowerCase();
        filtered = filtered.filter(function(rule) {
          return rule.brand_name &&
            rule.brand_name.toLowerCase().indexOf(brandFilter) !== -1;
        });
      }

      return filtered;
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        await this.$store.dispatch("marketing/fetchBrands", { limit: 0 });

        var rulesResponse = await axios.get("/api/marketing/matching-rules");
        this.matchingRules = rulesResponse.data.items || [];

        var deletedResponse = await axios.get("/api/marketing/matching-rules/deleted/list");
        this.deletedRules = deletedResponse.data.items || [];

        this.statusMsg = "Loaded " + this.masterBrands.length + " brands, " + this.matchingRules.length + " active rules, " + this.deletedRules.length + " deleted rules";
      } catch (error) {
        console.error("Error loading data:", error);
        this.statusMsg = "Error loading data: " + error.message;
      } finally {
        this.loading = false;
      }
    },
    async autoFillBrandNames() {
      if (this.masterBrands.length === 0) {
        this.statusMsg = "Please ensure master brands are loaded";
        return;
      }

      this.loading = true;
      try {
        var response = await axios.post("/api/marketing/matching-rules/auto-fill");
        this.statusMsg = response.data.message || "Auto-fill completed";
        await this.loadData();
      } catch (error) {
        console.error("Error auto-filling:", error);
        this.statusMsg = "Error: " + error.message;
      } finally {
        this.loading = false;
      }
    },
    async updateRuleBrand(ruleId, brandName) {
      try {
        var brandId = null;
        if (brandName) {
          var matchedBrand = this.masterBrands.find(function(b) {
            return b.brand_name === brandName;
          });
          if (matchedBrand) {
            brandId = matchedBrand.id;
          }
        }
        await axios.patch("/api/marketing/matching-rules/" + ruleId, {
          brand_id: brandId,
        });
        await this.loadData();
      } catch (error) {
        console.error("Error updating rule:", error);
        this.statusMsg = "Error: " + error.message;
      }
    },
    confirmDeleteMatchingRule(ruleId) {
      this.ruleToDelete = ruleId;
      this.deleteDialog = true;
    },
    async deleteMatchingRule() {
      if (!this.ruleToDelete) return;

      try {
        await axios.delete("/api/marketing/matching-rules/" + this.ruleToDelete);
        this.statusMsg = "Rule deleted (can be restored from Deleted Rules tab)";
        await this.loadData();
      } catch (error) {
        console.error("Error deleting rule:", error);
        this.statusMsg = "Error: " + error.message;
      } finally {
        this.deleteDialog = false;
        this.ruleToDelete = null;
      }
    },
    async restoreRule(ruleId) {
      try {
        await axios.post("/api/marketing/matching-rules/" + ruleId + "/restore");
        this.statusMsg = "Rule restored";
        await this.loadData();
      } catch (error) {
        console.error("Error restoring rule:", error);
        this.statusMsg = "Error: " + error.message;
      }
    },
    confirmHardDeleteRule(ruleId) {
      this.ruleToHardDelete = ruleId;
      this.hardDeleteDialog = true;
    },
    async hardDeleteRule() {
      if (!this.ruleToHardDelete) return;

      try {
        await axios.delete("/api/marketing/matching-rules/" + this.ruleToHardDelete + "/hard");
        this.statusMsg = "Rule permanently deleted";
        await this.loadData();
      } catch (error) {
        console.error("Error hard deleting rule:", error);
        this.statusMsg = "Error: " + error.message;
      } finally {
        this.hardDeleteDialog = false;
        this.ruleToHardDelete = null;
      }
    },
  },
};
</script>

<style scoped>
.warning-bg {
  background-color: #fff3e0;
}
</style>
